import { ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";

type AuthProductShellProps = {
  testId: string;
  title: string;
  description: string;
  children: ReactNode;
};

export function AuthProductShell({
  testId,
  title,
  description,
  children,
}: AuthProductShellProps) {
  return (
    <main
      data-testid={testId}
      className="relative h-[calc(100svh-9rem)] min-h-0 overflow-hidden bg-ink sm:h-[calc(100svh-5rem)]"
    >
      <img
        src="/pitch-broadcast.svg?v=provided-pitch"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 hidden h-full w-full object-cover dark:block"
      />
      <img
        src="/pitch-broadcast-light.svg?v=provided-pitch-light"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover dark:hidden"
      />
      <PitchFormation />

      <div className="relative z-10 flex h-full items-center justify-center p-4 sm:p-6 lg:justify-end lg:p-10">
        <section
          aria-label={title}
          className="flex max-h-full w-full max-w-md flex-col rounded border border-rule bg-surface p-3 sm:p-7"
        >
          <div className="min-h-0 overflow-y-auto">
            <header className="mb-3 flex flex-col gap-2 sm:mb-6">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-4xl">
                {title}
              </h1>
              <p className="max-w-xl text-sm leading-6 text-ink-muted">
                {description}
              </p>
            </header>
            <div className="rounded border border-rule bg-elevated p-3 sm:p-5">
              <div className="mb-4 flex items-center gap-2 border-b border-rule pb-3">
                <span className="flex h-8 w-8 items-center justify-center rounded bg-field text-ink">
                  <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-display text-sm font-semibold text-ink">
                    Account access
                  </p>
                  <p className="hidden text-xs text-ink-muted sm:block">
                    Sign in, verify email, or request team access.
                  </p>
                </div>
              </div>
              {children}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

const FORMATION_PLAYERS = [
  { x: 150, y: 380 },
  { x: 296, y: 238 },
  { x: 284, y: 330 },
  { x: 284, y: 430 },
  { x: 296, y: 522 },
  { x: 460, y: 302 },
  { x: 470, y: 458 },
  { x: 635, y: 380 },
  { x: 760, y: 252 },
  { x: 815, y: 380 },
  { x: 760, y: 508 },
] as const;

function PitchFormation() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1200 760"
      preserveAspectRatio="xMidYMid slice"
      className="pointer-events-none absolute inset-0 hidden h-full w-full md:block"
    >
      <g opacity="0.92">
        <g>
          {FORMATION_PLAYERS.map((player) => (
            <circle
              key={`${player.x}-${player.y}`}
              cx={player.x}
              cy={player.y}
              fill="rgb(var(--accent-rgb))"
              fillOpacity="0.88"
              r="13"
              stroke="rgb(var(--ink-inverse-rgb))"
              strokeOpacity="0.72"
              strokeWidth="2"
            />
          ))}
        </g>
        <g fill="rgb(var(--ink-muted-rgb))" opacity="0.7">
          <circle cx="384" cy="260" r="8" />
          <circle cx="392" cy="500" r="8" />
          <circle cx="575" cy="380" r="8" />
          <circle cx="710" cy="328" r="8" />
          <circle cx="710" cy="432" r="8" />
        </g>
      </g>
    </svg>
  );
}
